import React from 'react';

// Body content for the post:
// "The problems of finding contractors historically and how Plum tries to
//  address those problems"
function FindingContractors() {
  return (
    <>
      <p>
        Almost everyone who has renovated a home has a story about finding a
        contractor — and most of those stories are frustrating. The work itself
        can be great, but the process of getting from "I want to remodel" to "I
        hired the right person at a fair price" has historically been broken in
        predictable ways. Here is why, and how Plum tries to fix each part.
      </p>

      <h2>Problem 1: Your phone number becomes the product</h2>
      <p>
        The traditional way to find a contractor online is a lead-generation
        marketplace. You fill out a form, and your contact information is sold to
        several contractors at once. The result is a wave of robocalls and
        follow-up texts, often for weeks, from people you never chose to talk to.
        You wanted a kitchen quote; you got a spam problem.
      </p>
      <p>
        <strong>How Plum addresses it:</strong> Plum never hands out your phone
        number. You share your project once, and Plum sources and evaluates
        vetted local contractors on your behalf. There is no list of your details
        being resold, so there are no robocalls and no spam calls.
      </p>

      <h2>Problem 2: No two quotes describe the same project</h2>
      <p>
        When you ask three contractors to "quote a bathroom remodel," each one
        imagines a different bathroom. One assumes mid-grade tile, another
        assumes you are moving plumbing, a third leaves out the vanity. The bids
        come back wildly different and impossible to compare — not because one
        contractor is dishonest, but because there was never a shared definition
        of the work.
      </p>
      <p>
        <strong>How Plum addresses it:</strong> Plum produces a detailed,
        contractor-ready scope of work — every specification, measurement, and
        material decision in one document. When every contractor bids against the
        same scope, you finally get apples-to-apples quotes and can compete them
        on price and quality instead of guesswork.
      </p>

      <h2>Problem 3: Change orders quietly blow up the budget</h2>
      <p>
        The number on the original quote is rarely the number you pay. When
        details are decided mid-project — "what tile did you want again?" — each
        decision becomes a change order, and change orders are where budgets go
        to die. Underspecified projects practically guarantee them.
      </p>
      <p>
        <strong>How Plum addresses it:</strong> By guiding you to lock down the
        decisions <em>before</em> work begins, Plum shrinks the universe of
        mid-project surprises. The more that is settled and written down up
        front, the fewer change orders later — and the closer your final cost
        stays to the quote.
      </p>

      <h2>Problem 4: Homeowners are asked to be experts they aren't</h2>
      <p>
        To get a good quote you are expected to already know what you want, in
        the contractor's language. Most homeowners reasonably do not. That
        knowledge gap leads to vague requests, mismatched expectations, and work
        that does not match the vision in your head.
      </p>
      <p>
        <strong>How Plum addresses it:</strong> Plum runs a guided consultation
        that asks the right follow-up questions and builds your requirements with
        you, drawing on built-in renovation expertise. You end up with a
        professional scope and a clear cost estimate without needing to speak
        contractor fluently first. (We compare this guided approach to using a
        general chatbot in{' '}
        <a href="/blog/ai-design-vs-chatbots">
          How Plum makes AI design better than ChatGPT, Gemini, or Claude
        </a>
        .)
      </p>

      <h2>Problem 5: Trust and vetting are left to you</h2>
      <p>
        Reviews can be gamed, licenses are tedious to verify, and it is hard to
        know whether a contractor is a fit for your specific project. The
        vetting burden has traditionally fallen entirely on the homeowner.
      </p>
      <p>
        <strong>How Plum addresses it:</strong> Plum sources and evaluates vetted
        local contractors for your project, so the matching and screening work is
        shouldered for you rather than left as homework.
      </p>

      <h2>A better starting point</h2>
      <p>
        None of this removes the human judgment of choosing who to work with —
        and it should not. But it changes the position you negotiate from. With a
        clear scope, a realistic estimate, vetted matches, and your phone number
        kept private, you walk into the contractor conversation informed and in
        control instead of overwhelmed and oversold.
      </p>
      <p>
        Best of all, Plum is completely free for homeowners. You can build your
        scope, generate designs, and get a cost estimate before you ever talk to
        anyone.
      </p>
      <p>
        <a href="/home">Plan your renovation with Plum for free →</a>
      </p>
    </>
  );
}

export default FindingContractors;
